<script setup>
import { computed, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useContactFilterContext } from 'dashboard/components-next/filter/contactProvider.js';
import ConditionRow from 'dashboard/components-next/filter/ConditionRow.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({ disabled: { type: Boolean, default: false } });
// labelIds: number[]; filters: ConditionRow models (camelCase)
const labelIds = defineModel('labelIds', { type: Array, default: () => [] });
const filters = defineModel('filters', { type: Array, default: () => [] });
const { t } = useI18n();
const labels = useMapGetter('labels/getLabels');
const { filterTypes } = useContactFilterContext();
const conditionsRef = useTemplateRef('conditionsRef');

const DEFAULT_FILTER = {
  attributeKey: 'name',
  filterOperator: 'equal_to',
  values: '',
  queryOperator: 'and',
  attributeModel: 'standard',
};

const labelOptions = computed(() =>
  (labels.value || []).map(label => ({ value: label.id, label: label.title }))
);

const addFilter = () => filters.value.push({ ...DEFAULT_FILTER });
const removeFilter = index => filters.value.splice(index, 1);
const validate = () =>
  (conditionsRef.value || []).every(condition => condition.validate());
defineExpose({ validate });
</script>

<template>
  <div class="flex flex-col gap-5">
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">{{
        t('WASAPI_CAMPAIGNS.AUDIENCE.LABELS_LABEL')
      }}</label>
      <TagMultiSelectComboBox
        v-model="labelIds"
        :options="labelOptions"
        :placeholder="t('WASAPI_CAMPAIGNS.AUDIENCE.LABELS_PLACEHOLDER')"
        :disabled="disabled"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>
    <div class="flex flex-col gap-2">
      <label class="text-sm font-medium text-n-slate-12">{{
        t('WASAPI_CAMPAIGNS.AUDIENCE.FILTERS_LABEL')
      }}</label>
      <p class="text-xs text-n-slate-11">
        {{ t('WASAPI_CAMPAIGNS.AUDIENCE.FILTERS_HELP') }}
      </p>
      <div v-if="filters.length" class="flex flex-col gap-2">
        <ConditionRow
          v-for="(filter, index) in filters"
          :key="index"
          ref="conditionsRef"
          v-model:attribute-key="filter.attributeKey"
          v-model:filter-operator="filter.filterOperator"
          v-model:values="filter.values"
          v-model:query-operator="filter.queryOperator"
          :filter-types="filterTypes"
          :show-query-operator="index !== filters.length - 1"
          @remove="removeFilter(index)"
        />
      </div>
      <div>
        <Button
          :label="t('WASAPI_CAMPAIGNS.AUDIENCE.ADD_FILTER')"
          icon="i-lucide-plus"
          variant="faded"
          color="slate"
          size="sm"
          :disabled="disabled"
          @click="addFilter"
        />
      </div>
    </div>
  </div>
</template>
